// ArchetypeClassifier.js
// Component for classifying guides according to the 8 Travel DNA Archetypes

import React, { useState } from 'react';
import { Wand2, Loader, CheckCircle, AlertCircle, Info, ChevronDown, ChevronUp, Edit2, Save, X } from 'lucide-react';

const ARCHETYPES = [
  { id: 'excited_experientialist', name: 'Excited Experientialist', emoji: '🎉', description: 'The YOLO Traveler - High energy, FOMO-driven, social' },
  { id: 'memory_maker', name: 'Memory Maker', emoji: '❤️', description: 'The Bonding Traveler - Family-friendly, nostalgic, warm' },
  { id: 'pioneering_pathfinder', name: 'Pioneering Pathfinder', emoji: '🗺️', description: 'The Explorer - Adventurous, authentic, off-beaten-path' },
  { id: 'travel_tech_fluencer', name: 'Travel Tech-fluencer', emoji: '⚡', description: 'The Optimizer - Efficient, data-driven, maximizer' },
  { id: 'cultural_sponge', name: 'Cultural Sponge', emoji: '📚', description: 'The Learner - Intellectual, scholarly, deep' },
  { id: 'aesthetic_hunter', name: 'Aesthetic Hunter', emoji: '📸', description: 'The Instagrammer - Visual, design-focused, curated' },
  { id: 'adrenaline_junkie', name: 'Adrenaline Junkie', emoji: '🏔️', description: 'The Thrill Seeker - Risk-tolerant, challenge-driven' },
  { id: 'zen_master', name: 'Zen Master', emoji: '🧘', description: 'The Recharger - Peaceful, mindful, restorative' }
];

export const ArchetypeClassifier = ({ itinerary, setItinerary }) => {
  const [isClassifying, setIsClassifying] = useState(false);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAllScores, setShowAllScores] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScores, setEditedScores] = useState({});

  const classification = itinerary.metadata?.archetypeClassification;
  const hasClassification = classification && classification.primaryArchetype;

  const handleClassify = async () => {
    setIsClassifying(true);
    setError(null);

    try {
      // Call the classification service
      const result = await classifyGuide(itinerary);

      // Update the itinerary metadata with classification
      setItinerary(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          archetypeClassification: {
            primaryArchetype: result.primaryArchetype,
            secondaryArchetypes: result.secondaryArchetypes,
            archetypeAlignment: result.archetypeAlignment,
            contentAttributes: result.contentAttributes,
            interestTags: result.interestTags,
            engagementFactors: result.engagementFactors,
            classificationDate: new Date().toISOString(),
            isManuallyEdited: false
          }
        }
      }));

      console.log('✅ Guide classified successfully');
    } catch (err) {
      console.error('❌ Classification failed:', err);
      setError(err.message || 'Failed to classify guide. Please try again.');
    } finally {
      setIsClassifying(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    // Initialize with existing scores or all zeros
    const initialScores = classification?.archetypeAlignment || {
      excited_experientialist: 0,
      memory_maker: 0,
      pioneering_pathfinder: 0,
      travel_tech_fluencer: 0,
      cultural_sponge: 0,
      aesthetic_hunter: 0,
      adrenaline_junkie: 0,
      zen_master: 0
    };
    setEditedScores(initialScores);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedScores({});
  };

  const handleSaveManualScores = () => {
    // Find primary archetype (highest score)
    const primaryArchetype = Object.entries(editedScores)
      .sort((a, b) => b[1] - a[1])[0]?.[0];

    // Find secondary archetypes (scores >= 0.6)
    const secondaryArchetypes = Object.entries(editedScores)
      .filter(([id, score]) => id !== primaryArchetype && score >= 0.6)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([id]) => id);

    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        archetypeClassification: {
          ...classification,
          primaryArchetype,
          secondaryArchetypes,
          archetypeAlignment: editedScores,
          classificationDate: new Date().toISOString(),
          isManuallyEdited: true
        }
      }
    }));

    setIsEditing(false);
    setEditedScores({});
  };

  const handleScoreChange = (archetypeId, value) => {
    setEditedScores(prev => ({
      ...prev,
      [archetypeId]: parseFloat(value)
    }));
  };

  const getPrimaryArchetype = () => {
    if (!hasClassification) return null;
    return ARCHETYPES.find(a => a.id === classification.primaryArchetype);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'bg-green-500';
    if (score >= 0.6) return 'bg-blue-500';
    if (score >= 0.4) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  const getScoreLabel = (score) => {
    if (score >= 0.8) return 'Excellent';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Moderate';
    if (score >= 0.2) return 'Poor';
    return 'Very Poor';
  };

  const primaryArchetype = getPrimaryArchetype();
  const sortedArchetypes = hasClassification
    ? ARCHETYPES.map(archetype => ({
        ...archetype,
        score: (isEditing ? editedScores : classification.archetypeAlignment)[archetype.id] || 0
      })).sort((a, b) => b.score - a.score)
    : [];

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Travel DNA Archetype Classification</h3>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-gray-500 hover:text-gray-700"
            title="What is this?"
          >
            <Info className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2">
          {!isEditing && (
            <button
              onClick={handleStartEditing}
              className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              {hasClassification ? 'Edit Manually' : 'Set Manually'}
            </button>
          )}
          <button
            onClick={handleClassify}
            disabled={isClassifying || isEditing}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            {isClassifying ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Classifying...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                {hasClassification ? 'Re-classify' : 'Classify with AI'}
              </>
            )}
          </button>
        </div>
      </div>

      {showDetails && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
          <p className="text-blue-900">
            <strong>Archetype Classification</strong> analyzes your guide's content, tone, pacing, and focus to determine
            which of the 8 Travel DNA Archetypes it best matches. This helps personalize guide recommendations for users
            based on their travel preferences.
          </p>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Manual Editing Mode */}
      {isEditing && (
        <div className="space-y-3 p-4 bg-white rounded-lg border-2 border-blue-400">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold">Manual Score Adjustment</h4>
            <div className="flex gap-2">
              <button
                onClick={handleCancelEditing}
                className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 flex items-center gap-1 text-sm"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSaveManualScores}
                className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1 text-sm"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {ARCHETYPES.map(archetype => (
              <div key={archetype.id} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="text-lg">{archetype.emoji}</span>
                    <span className="font-medium">{archetype.name}</span>
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={editedScores[archetype.id]?.toFixed(2) || '0.00'}
                    onChange={(e) => handleScoreChange(archetype.id, e.target.value)}
                    className="w-20 px-2 py-1 border rounded text-sm"
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={editedScores[archetype.id] || 0}
                  onChange={(e) => handleScoreChange(archetype.id, e.target.value)}
                  className="w-full h-2 accent-purple-600"
                />
              </div>
            ))}
          </div>

          <div className="text-xs text-gray-600 mt-3 p-2 bg-blue-50 rounded">
            <strong>Tip:</strong> Primary archetype will be the one with the highest score.
            Secondary archetypes are those with scores ≥ 0.6.
          </div>
        </div>
      )}

      {!isEditing && hasClassification && primaryArchetype && (
        <div className="space-y-4">
          {/* Primary Archetype Display */}
          <div className="p-4 bg-white rounded-lg border-2 border-purple-300 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-3xl">{primaryArchetype.emoji}</span>
                <div>
                  <h4 className="font-semibold text-lg">{primaryArchetype.name}</h4>
                  <p className="text-sm text-gray-600">{primaryArchetype.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-600">
                  {Math.round(classification.archetypeAlignment[primaryArchetype.id] * 100)}%
                </div>
                <div className="text-xs text-gray-500">Match Score</div>
              </div>
            </div>

            {classification.secondaryArchetypes && classification.secondaryArchetypes.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-600 mb-2">Secondary Archetypes:</div>
                <div className="flex gap-2 flex-wrap">
                  {classification.secondaryArchetypes.map(archId => {
                    const arch = ARCHETYPES.find(a => a.id === archId);
                    return arch ? (
                      <span key={archId} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
                        {arch.emoji} {arch.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Content Attributes */}
          {classification.contentAttributes && (
            <div className="p-3 bg-white rounded border border-gray-200">
              <h5 className="font-medium text-sm mb-2">Content Attributes</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Tone:</span>{' '}
                  <span className="font-medium">{classification.contentAttributes.tone}</span>
                </div>
                <div>
                  <span className="text-gray-600">Pacing:</span>{' '}
                  <span className="font-medium">{classification.contentAttributes.pacing}</span>
                </div>
                <div>
                  <span className="text-gray-600">Structure:</span>{' '}
                  <span className="font-medium">{classification.contentAttributes.structure}</span>
                </div>
                <div>
                  <span className="text-gray-600">Difficulty:</span>{' '}
                  <span className="font-medium">{classification.contentAttributes.difficulty}</span>
                </div>
              </div>
            </div>
          )}

          {/* Interest Tags */}
          {classification.interestTags && classification.interestTags.length > 0 && (
            <div className="p-3 bg-white rounded border border-gray-200">
              <h5 className="font-medium text-sm mb-2">Interest Tags</h5>
              <div className="flex gap-2 flex-wrap">
                {classification.interestTags.map(tag => (
                  <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                    {tag.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* All Archetype Scores */}
          <div className="p-3 bg-white rounded border border-gray-200">
            <button
              onClick={() => setShowAllScores(!showAllScores)}
              className="w-full flex items-center justify-between text-sm font-medium mb-2"
            >
              <span>All Archetype Scores</span>
              {showAllScores ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showAllScores && (
              <div className="space-y-2 mt-3">
                {sortedArchetypes.map(archetype => (
                  <div key={archetype.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1">
                        <span>{archetype.emoji}</span>
                        <span>{archetype.name}</span>
                      </span>
                      <span className="text-xs font-medium">
                        {Math.round(archetype.score * 100)}% - {getScoreLabel(archetype.score)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getScoreColor(archetype.score)}`}
                        style={{ width: `${archetype.score * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Classification Date */}
          {classification.classificationDate && (
            <div className="text-xs text-gray-500 text-center">
              Classified on {new Date(classification.classificationDate).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {!hasClassification && !isClassifying && (
        <div className="text-center py-8 text-gray-500">
          <p className="mb-2">This guide hasn't been classified yet.</p>
          <p className="text-sm">Click "Classify Guide" to analyze the content and assign archetype scores.</p>
        </div>
      )}
    </div>
  );
};

// Classification service function
async function classifyGuide(itinerary) {
  // Extract guide content for classification
  const guideContent = {
    name: itinerary.metadata.name,
    description: itinerary.metadata.description,
    theme: itinerary.metadata.theme,
    targetAudience: itinerary.metadata.targetAudience,
    duration: itinerary.metadata.duration,
    difficulty: itinerary.metadata.difficulty,
    points: itinerary.points?.map(p => ({
      name: p.name,
      description: p.description,
      inDepthDescription: p.inDepthDescription,
      category: p.category,
      historicalContext: p.historicalContext,
      culturalSignificance: p.culturalSignificance,
      tips: p.tips
    })) || []
  };

  // Call Backend Cloud Function
  const functionUrl = process.env.REACT_APP_CLASSIFY_FUNCTION_URL ||
                      'https://backend-classifyguidearchetype-ybvgkh7xrq-uc.a.run.app';

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ guideContent })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Classification failed: ${response.statusText}`);
  }

  const result = await response.json();
  return result;
}

export default ArchetypeClassifier;
